import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, MenuItem, Select, TextField } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { CrmIntakeView, crmApi } from '../../../features/crm/crmApi'
import { Chip, Chips, CrmDialog, ErrorText, FieldLabel, Hint, Muted, OutlineButton, PrimaryButton } from './crmUi'
import { addDaysKey, addMonthsKey, digits, fmtDate, formatPhone, money, todayKey } from './format'

const TYPES = ['MONTHLY', 'INTERNAL_INSTALLMENT']

// «＋ Бұрынғы оқушыны қосу»: платформада учится, графика платежей нет. Модератор и РОП.
const LegacyDialog: React.FC<{ rop: boolean; onClose: () => void }> = ({ rop, onClose }) => {
    const { formatMessage } = useIntl()
    const [q, setQ] = useState('')
    const [dq, setDq] = useState('')
    const [pick, setPick] = useState<CrmIntakeView | null>(null)
    const [payType, setPayType] = useState('MONTHLY')
    const [sellerId, setSellerId] = useState(0)
    const [sum, setSum] = useState('')
    const [date, setDate] = useState(() => addDaysKey(todayKey(), 7))
    const [left, setLeft] = useState(3)
    const [err, setErr] = useState('')

    useEffect(() => {
        const t = setTimeout(() => setDq(q.trim()), 300)
        return () => clearTimeout(t)
    }, [q])

    const { data: hits = [], isFetching } = crmApi.useSearchLegacyCandidatesQuery(dq, { skip: dq.length < 2 || !!pick })
    // менеджеры: у РОП - список с фильтром активных, у модератора отдельного списка нет - берём продавцов из заявок
    const { data: managers = [] } = crmApi.useGetManagersQuery({}, { skip: !rop })
    const { data: all = [] } = crmApi.useListIntakesQuery({ tab: 'ALL' }, { skip: rop })
    const options = useMemo(() => {
        if (rop) return managers.filter((m) => !m.firedAt).map((m) => ({ id: m.userId, name: m.name }))
        const seen = new Map<number, string>()
        all.forEach((x) => x.sellerId && !seen.has(x.sellerId) && seen.set(x.sellerId, x.sellerName || String(x.sellerId)))
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }))
    }, [rop, managers, all])
    const [add, { isLoading }] = crmApi.useAddLegacyInstallmentMutation()

    const dates = date && left ? Array.from({ length: left }, (_, i) => addMonthsKey(date, i)) : []

    const submit = async () => {
        const e = !pick
            ? formatMessage({ id: 'crmLegPickStudent' })
            : !sellerId
            ? formatMessage({ id: 'crmLegPickManager' })
            : !Number(sum)
            ? formatMessage({ id: 'crmLegSumRequired' })
            : !date
            ? formatMessage({ id: 'crmLegDateRequired' })
            : ''
        setErr(e)
        if (e || !pick) return
        try {
            await add({ intakeId: pick.id, payType, sellerId, monthlySum: Number(sum), nextDate: date, monthsLeft: left }).unwrap()
            toast.success(
                formatMessage(
                    { id: 'crmLegAddedToast' },
                    { name: pick.data.studentName, manager: options.find((o) => o.id === sellerId)?.name || '-', date: fmtDate(date) },
                ),
            )
            onClose()
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <CrmDialog title={formatMessage({ id: 'crmLegTitle' })} sub={formatMessage({ id: 'crmLegSub' })} onClose={onClose}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '14px' }}>
                <Box>
                    <FieldLabel required>{formatMessage({ id: 'crmColStudent' })}</FieldLabel>
                    {pick ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${ui.border}`, borderRadius: '10px', padding: '8px 12px' }}>
                            <Box sx={{ fontSize: '13.5px', overflowWrap: 'anywhere' }}>
                                <b>{pick.data.studentName}</b> · {formatPhone(pick.data.studentPhone)}
                            </Box>
                            <OutlineButton size="small" onClick={() => setPick(null)}>{formatMessage({ id: 'crmLegChange' })}</OutlineButton>
                        </Box>
                    ) : (
                        <>
                            <TextField size="small" fullWidth value={q} autoComplete="off" placeholder={formatMessage({ id: 'crmLegSearchPlaceholder' })} onChange={(e) => setQ(e.target.value)} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px', maxHeight: 200, overflowY: 'auto' }}>
                                {hits.slice(0, 8).map((x) => (
                                    <Box
                                        key={x.id}
                                        component="button"
                                        type="button"
                                        onClick={() => setPick(x)}
                                        sx={{ textAlign: 'left', border: `1px solid ${ui.border}`, background: ui.card, borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', font: 'inherit', fontSize: '13px' }}
                                    >
                                        {x.data.studentName} <Muted sx={{ display: 'inline' }}>{formatPhone(x.data.studentPhone)}{x.sellerName ? ` · ${x.sellerName}` : ''}</Muted>
                                    </Box>
                                ))}
                                {dq.length >= 2 && !isFetching && !hits.length && <Hint>{formatMessage({ id: 'crmLegNotFound' })}</Hint>}
                            </Box>
                        </>
                    )}
                </Box>
                <Box>
                    <FieldLabel>{formatMessage({ id: 'crmPayType' })}</FieldLabel>
                    <Chips>
                        {TYPES.map((t) => (
                            <Chip key={t} on={payType === t} onClick={() => setPayType(t)}>
                                {formatMessage({ id: `crmLegType_${t}` })}
                            </Chip>
                        ))}
                    </Chips>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <FieldLabel required>{formatMessage({ id: 'crmLegManager' })}</FieldLabel>
                        <Select size="small" fullWidth displayEmpty value={sellerId} onChange={(e) => setSellerId(Number(e.target.value))}>
                            <MenuItem value={0} disabled>{formatMessage({ id: 'crmChoose' })}</MenuItem>
                            {options.map((o) => (
                                <MenuItem key={o.id} value={o.id}>{o.name || '-'}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box>
                        <FieldLabel required>{formatMessage({ id: 'crmLegMonthlySum' })}</FieldLabel>
                        <TextField size="small" fullWidth value={sum} placeholder="40000" inputProps={{ inputMode: 'numeric' }} onChange={(e) => setSum(digits(e.target.value))} />
                    </Box>
                    <Box>
                        <FieldLabel required>{formatMessage({ id: 'crmNextPayDate' })}</FieldLabel>
                        <TextField type="date" size="small" fullWidth value={date} onChange={(e) => setDate(e.target.value)} />
                    </Box>
                    <Box>
                        <FieldLabel required>{formatMessage({ id: 'crmLegMonthsLeft' })}</FieldLabel>
                        <Select size="small" fullWidth value={left} onChange={(e) => setLeft(Number(e.target.value))}>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((k) => (
                                <MenuItem key={k} value={k}>{k}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>
                {dates.length > 0 && (
                    <Hint>
                        {formatMessage({ id: 'crmLegDates' })}: <b>{dates.map(fmtDate).join(' · ')}</b>
                        {Number(sum) ? ` · ${formatMessage({ id: 'crmLegEach' }, { sum: money(Number(sum)) })}` : ''}
                    </Hint>
                )}
                <ErrorText text={err} />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <OutlineButton onClick={onClose}>{formatMessage({ id: 'cancel' })}</OutlineButton>
                    <PrimaryButton disabled={isLoading} onClick={submit}>{formatMessage({ id: 'crmLegAdd' })}</PrimaryButton>
                </Box>
            </Box>
        </CrmDialog>
    )
}

export default LegacyDialog
